/**
 * Jain Gaurav Yatra — Volunteers CRUD API.
 * Backend: /jain_gaurav_yatra/volunteers/  (cookie-session auth + X-Tenant-ID, JSON body).
 */
import { extractAdminTokens, getStoredAdminSession } from "@/lib/admin-login-api";

function apiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";
}

function tenantId(): string {
  return (import.meta.env.VITE_X_TENANTID as string | undefined) ?? "";
}

function jsonHeaders(): HeadersInit {
  const id = tenantId();
  const { access } = extractAdminTokens(getStoredAdminSession()?.session);
  return {
    "Content-Type": "application/json",
    ...(id ? { "X-Tenant-ID": id } : {}),
    ...(access ? { Authorization: `Bearer ${access}` } : {}),
  };
}

function errMessage(data: unknown, status: number): string {
  const d = data as Record<string, unknown>;
  const pick = (v: unknown) => (typeof v === "string" ? v : null);
  const msg =
    pick(d?.message) ??
    pick(d?.detail) ??
    (Array.isArray(d?.non_field_errors) ? pick((d.non_field_errors as string[])[0]) : null);
  if (msg) {return msg;}
  try {
    return JSON.stringify(data);
  } catch {
    return `Request failed (${status})`;
  }
}

const VOLUNTEERS_PATH = "/jain_gaurav_yatra/volunteers/";

/** Page-facing volunteer shape (matches the existing admin.volunteers UI fields). */
export interface Volunteer {
  id: string;
  name: string;
  phone: string;
  languages: string[];
  experience_years: number;
  charges_per_day: number;
  bio_en: string;
  bio_hi: string;
  bio_gu: string;
  active: boolean;
}

/** Fields accepted when creating/updating a volunteer. */
export interface VolunteerWrite {
  name: string;
  phone: string;
  languages: string[];
  experience_years: number;
  charges_per_day: number;
  bio_en: string;
  bio_hi: string;
  bio_gu: string;
  active: boolean;
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function str(v: unknown): string {
  return v === null || v === undefined ? "" : String(v);
}

/** API stores languages as a comma-separated string; the UI uses an array. */
function parseLanguages(v: unknown): string[] {
  if (Array.isArray(v)) {return v.map((x) => str(x).trim()).filter(Boolean);}
  return str(v)
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

function toVolunteer(row: Record<string, unknown>): Volunteer {
  return {
    id: str(row.id),
    name: str(row.name),
    phone: str(row.phone),
    languages: parseLanguages(row.languages),
    experience_years: num(row.experience_years),
    charges_per_day: num(row.charges_per_day_inr ?? row.charges_per_day),
    bio_en: str(row.bio_en),
    bio_hi: str(row.bio_hi),
    bio_gu: str(row.bio_gu),
    active: row.active !== false,
  };
}

function toBody(p: VolunteerWrite): string {
  return JSON.stringify({
    name: p.name,
    phone: p.phone,
    languages: p.languages.join(", "),
    experience_years: p.experience_years,
    charges_per_day_inr: p.charges_per_day,
    bio_en: p.bio_en,
    bio_hi: p.bio_hi,
    bio_gu: p.bio_gu,
    active: p.active,
  });
}

async function parseJson(res: Response): Promise<unknown> {
  return res.json().catch(() => ({}));
}

export async function listVolunteers(): Promise<Volunteer[]> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const res = await fetch(`${base}${VOLUNTEERS_PATH}`, {
    method: "GET",
    headers: jsonHeaders(),
    credentials: "include",
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  const rows = Array.isArray(data)
    ? data
    : Array.isArray((data as { results?: unknown[] })?.results)
      ? (data as { results: unknown[] }).results
      : [];
  return (rows as Record<string, unknown>[]).map(toVolunteer);
}

export async function createVolunteer(payload: VolunteerWrite): Promise<Volunteer> {
  const base = apiBase();
  const res = await fetch(`${base}${VOLUNTEERS_PATH}`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "include",
    body: toBody(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toVolunteer(data as Record<string, unknown>);
}

export async function updateVolunteer(id: string, payload: VolunteerWrite): Promise<Volunteer> {
  const base = apiBase();
  const res = await fetch(`${base}${VOLUNTEERS_PATH}${id}/`, {
    method: "PATCH",
    headers: jsonHeaders(),
    credentials: "include",
    body: toBody(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toVolunteer(data as Record<string, unknown>);
}

export async function deleteVolunteer(id: string): Promise<void> {
  const base = apiBase();
  const res = await fetch(`${base}${VOLUNTEERS_PATH}${id}/`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "include",
  });
  if (!res.ok && res.status !== 204) {
    const data = await parseJson(res);
    throw new Error(errMessage(data, res.status));
  }
}
